var CoreObject = require("enyo/CoreObject"),
    RepeaterArranger,
    dom        = require("enyo/dom"),
    kind       = require("enyo/kind"),
    logger     = require("enyo/logger"),
    utils      = require("enyo/utils");

require("enyo-ui");

/**
 * An abstract arranger for [Repeater]{@link enyo-ui.Repeater} controls. It
 * defines some required methods and some common functionality.
 *
 * This class is not meant to be used directly, but rather to be extended to
 * implement particular arrangement of repeater children relative to the
 * top-left corner of the child container.
 *
 * @ui
 * @class enyo-ui.RepeaterArranger
 * @abstract
 * @public
 */
RepeaterArranger = module.exports = kind(/** @lends  enyo-ui.RepeaterArranger.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.RepeaterArranger",

    /**
     * @private
     */
    kind: CoreObject,

    /**
     * The repeater that's currently using this arranger.
     *
     * @type {?enyo-ui.Repeater}
     * @private
     */
    repeater: null,

    /**
     * CSS class that's added to the [Repeater]{@link enyo-ui.Repeater} using
     * this arranger.
     *
     * @type    {string}
     * @default ""
     * @public
     */
    arrangerClass: "",

    /**
     * Default options for the arranger.
     *
     * @type {Object}
     * @protected
     */
    options: null,

    /**
     * Sets reference to repeater, adds arrangerClass to repeater and imports
     * arrangerOptions from repeater.
     *
     * @param {enyo.Control}  repeater  The repeater
     * @private
     */
    constructor: function(repeater) {
        this.repeater = repeater;

        if (repeater) {
            this.options = utils.mixin(utils.clone(this.options || {}), repeater.arrangerOptions);
            repeater.addClass(this.arrangerClass);
        }
    },

    /**
     * Removes arrangerClass from repeater.
     *
     * @private
     */
    destroy: function() {
        if (this.repeater) {
            this.repeater.removeClass(this.arrangerClass);
        }
    },

    /**
     * Convenience method for retrieving options, automatically resolves
     * computed options.
     *
     * @param  {string}  option  The option
     * @return {*}       The option's value
     * @public
     */
    getOption: function(option) {
        var value;

        value = this.options[option];

        return utils.isFunction(value) ? value.call(this) : value;
    },

    /**
     * Calculate any properties of the arrangement that depend on the dimensions
     * of the repeater here.
     *
     * @public
     */
    reflow: function() {
        logger.error("RepeaterArranger must implement a `reflow` method");
    },

    /**
     * Gets the amount of placeholders needed to fill a viewport of the provided
     * dimensions.
     *
     * @param  {number}  width   The width
     * @param  {number}  height  The height
     * @return {number}  The amount of placeholders.
     * @public
     */
    getPlaceholderCount: function(width, height) {
        return logger.error("RepeaterArranger must implement a `getPlaceholderCount` method", width, height);
    },

    /**
     * Get the size of the entire arrangement. Should have at least a width or a
     * height property and at most both width and height.
     *
     * @return {Object}  The arrangement size.
     * @public
     */
    getArrangementSize: function() {
        return logger.error("RepeaterArranger must implement a `getArrangementSize` method");
    },

    /**
     * Gets a sorted set of indexes of children within the arrangement that
     * intersect with the provided bounds.
     *
     * The incoming bounds should be defined in pixels, relative to the top-left
     * corner of the childContainer.
     *
     * @param  {Object}  bounds         The bounds
     * @param  {number}  bounds.top     The top edge of the bounding box in
     *                                  pixels from the top of the grid
     * @param  {number}  bounds.left    The left edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.right   The right edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.bottom  The bottom edge of the bounding box in
     *                                  pixels from the top of the grid
     * @return {Array}   Array of indexes
     * @public
     */
    getArrangementIndexes: function(bounds) {
        return logger.error("RepeaterArranger must implement a `getArrangementIndexes` method", bounds);
    },

    /**
     * Returns the distance (in pixels) between the top edge of the child
     * control at the given index, and the top edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getOffsetTop: function(index) {
        return logger.error("RepeaterArranger must implement a `getOffsetTop` method", index);
    },

    /**
     * Returns the distance (in pixels) between the left edge of the child
     * control at the given index, and the left edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getOffsetLeft: function(index) {
        return logger.error("RepeaterArranger must implement a `getOffsetLeft` method", index);
    },

    /**
     * Gets the index of the child positioned closest to the provided
     * coordinates.
     *
     * The incoming coordinates should be defined in pixels, relative to the
     * top-left corner of the childContainer.
     *
     * This method should not cause any paints or reflows.
     *
     * @param  {number}   x  The coordinates on the `x` axis.
     * @param  {number}   y  The coordinates on the `y` axis.
     * @return {?number}  The index, or `null` if repeater has 0 length
     * @public
     */
    getNearestIndex: function(x, y) {
        return logger.error("RepeaterArranger must implement a `getNearestIndex` method", x, y);
    },

    /**
     * Returns the scroll offset (in pixels) that would align the child arranged
     * at the provided index with the top of a scrolled view of the
     * childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollTop: function(index) {
        return logger.error("RepeaterArranger must implement a `getScrollTop` method", index);
    },

    /**
     * Returns the scroll offset (in pixels) that would align the child arranged
     * at the provided index with the left side of a scrolled view of the
     * childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollLeft: function(index) {
        return logger.error("RepeaterArranger must implement a `getScrollLeft` method", index);
    },

    /**
     * Memorize the size of the child associated with the provided index.
     * Returns whether the size at the index changed, thus altering the flow of
     * the arrangement. This method is meant for internal use only.
     *
     * @param  {number}   index  The index
     * @param  {Object}   size   The size
     * @return {boolean}  `true` if flow changed, `false` otherwise
     * @protected
     */
    setSizeAtIndex: function(index, size) {
        return logger.error("RepeaterArranger must implement a `setSizeAtIndex` method", index, size);
    },

    /**
     * Unsets any size stored for the provided index, returning to the default
     * size. Returns whether the size at the index changed, thus altering the
     * flow of the arrangement. This method is meant for internal use only.
     *
     * @param  {number}   index  The index
     * @return {boolean}  `true` if flow changed, `false` otherwise
     * @protected
     */
    unsetSizeAtIndex: function(index) {
        return logger.error("RepeaterArranger must implement a `unsetSizeAtIndex` method", index);
    },

    /**
     * Gets the size stored for the provided index, if any. This method is meant
     * for internal use only.
     *
     * @param  {number}  index  The index
     * @return {*}       The size if set, `null` otherwise
     * @protected
     */
    getSizeAtIndex: function(index) {
        return logger.error("RepeaterArranger must implement a `getSizeAtIndex` method", index);
    },

    /**
     * Renders the child if needed. If children determine their own size,
     * measure their bounds here and store them on the child as
     * `repeaterBounds`.
     *
     * @param {enyo.Control}  child  The child
     * @public
     */
    drawChild: function(child) {
        logger.error("RepeaterArranger must implement a `drawChild` method", child);
    },

    /**
     * Applies a css transform to position the child. Stores arrangement
     * properties on the child. Returns whether arranging the child at this
     * index changed the flow of the arrangement as a whole.
     *
     * @param  {enyo.Control}  child  The child
     * @param  {number}        index  The index
     * @return {boolean}       `true` if flow changed, `false` otherwise
     * @public
     */
    arrangeChild: function(child, index) {
        var accelerate = this.repeater.accelerate,
            arrangement,
            transforms = child.domTransforms || {},
            translate  = {},
            flowChanged;

        // Update sizes
        flowChanged = this.setSizeAtIndex(index, child.repeaterSize);

        // Calculate new arrangement
        arrangement = {
            top:  this.getOffsetTop(index),
            left: this.getOffsetLeft(index)
        };

        // Calculate translate transform
        if (accelerate) {
            translate.translateZ = "0";
        }

        if (arrangement.left) {
            translate.translateX = arrangement.left + "px";
        }

        if (arrangement.top) {
            translate.translateY = arrangement.top + "px";
        }

        // Ensure the translation transform will be be evaluated first. This
        // prevents scale transform from interfering with the arrangement.
        delete transforms.translateX;
        delete transforms.translateY;
        delete transforms.translateZ;
        child.domTransforms = utils.mixin(translate, transforms);
        dom.transformsToDom(child);

        // Memorize applied arrangement
        child.set("repeaterIndex", index);
        child.set("repeaterArrangement", arrangement);

        return flowChanged;
    },

    /**
     * Removes the arrangements' css transform from the child and unsets the
     * child's arrangement properties. Returns whether arranging the child at
     * this index changed the flow of the arrangement as a whole.
     *
     * @param  {enyo.Control}  child  The child
     * @return {boolean}       `true` if flow changed, `false` otherwise
     * @public
     */
    unarrangeChild: function(child) {
        var flowChanged;

        // Update sizes
        flowChanged = this.unsetSizeAtIndex(child.repeaterIndex);

        // Undo translation transforms
        if (child.domTransforms) {
            delete child.domTransforms.translateX;
            delete child.domTransforms.translateY;
            delete child.domTransforms.translateZ;
            dom.transformsToDom(child);
        }

        // Clear arrangement
        child.set("repeaterIndex", null);
        child.set("repeaterArrangement", null);

        return flowChanged;
    }

});

/**
 * This method allows enyo.kind to extend the options hash when creating super
 * kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof RepeaterArranger
 * @static
 * @private
 */
RepeaterArranger.concat = function(ctor, props) {
    var proto = ctor.prototype || ctor,
        options;

    if (props.options) {
        options       = proto.options ? utils.clone(proto.options) : {};
        proto.options = utils.mixin(options, props.options);
        delete props.options;
    }
};
